import { sortBy } from "lodash";
import moment from "moment";
import {
    AttributeOption,
    Coverage,
    CoverageOption,
    Driver,
    MonthlyReport,
    MonthlyReportAvailableMonth,
    MonthlyReportChanges,
    MonthlyReportCoveragePremium,
    MonthlyReportCoverageUnitPremiums,
    MonthlyReportData,
    MonthlyReportDataUnit,
    MonthlyReportDriverCoverageCost,
    MonthlyReportPremiumLine,
    MonthlyReportStatus,
    MonthlyReportUnitPremium,
    PolicyLayer,
    PolicyLayerComparison,
    Tractor,
    Trailer,
} from "../../northstar";

export class MonthlyReportHelper {
    /** Get the units that will affect the premium of the policy, taking into account the 15 day rule if necessary */
    public static getFinalUnits(
        data: Pick<MonthlyReportData, "month" | "year"> & {
            equipment: Pick<MonthlyReportData["equipment"], "tractors" | "trailers" | "drivers">;
            endLayer: { policy: Pick<MonthlyReportData["endLayer"]["policy"], "attributes"> };
        },
        coverage: Pick<Coverage, "coverageOptionId"> & {
            linkedTractors: Pick<Coverage["linkedTractors"][number], "tractorId">[];
            linkedTrailers: Pick<Coverage["linkedTrailers"][number], "trailerId">[];
            linkedDrivers: Pick<Coverage["linkedDrivers"][number], "driverId">[];
        }
    ): {
        tractors: MonthlyReportDataUnit<Tractor>[];
        trailers: MonthlyReportDataUnit<Trailer>[];
        drivers: MonthlyReportDataUnit<Driver>[];
    } {
        const is15DayRuleApplied = data.endLayer.policy.attributes.some(
            (att) => att.attributeOptionId === AttributeOption.Id.FIFTEEN_DAY_RULE_APPLIES
        );
        const fifteenthDayOfMonth = moment()
            .date(15)
            .month(data.month - 1)
            .year(data.year);

        let tractors: MonthlyReportDataUnit<Tractor>[] = [];
        let trailers: MonthlyReportDataUnit<Trailer>[] = [];
        let drivers: MonthlyReportDataUnit<Driver>[] = [];

        if (is15DayRuleApplied) {
            tractors = data.equipment.tractors.filter((t) => {
                const isActive = coverage.linkedTractors.some((lt) => lt.tractorId === t.id);
                return MonthlyReportHelper.isUnitActiveAt15DayCutoff(
                    t,
                    coverage.coverageOptionId,
                    isActive,
                    fifteenthDayOfMonth
                );
            });

            trailers = data.equipment.trailers.filter((t) => {
                const isActive = coverage.linkedTrailers.some((lt) => lt.trailerId === t.id);
                return MonthlyReportHelper.isUnitActiveAt15DayCutoff(
                    t,
                    coverage.coverageOptionId,
                    isActive,
                    fifteenthDayOfMonth
                );
            });

            drivers = data.equipment.drivers.filter((t) => {
                const isActive = coverage.linkedDrivers.some((lt) => lt.driverId === t.id);
                return MonthlyReportHelper.isUnitActiveAt15DayCutoff(
                    t,
                    coverage.coverageOptionId,
                    isActive,
                    fifteenthDayOfMonth
                );
            });
        } else {
            tractors = data.equipment.tractors.filter((t) => coverage.linkedTractors.some((lt) => lt.tractorId === t.id));
            trailers = data.equipment.trailers.filter((t) => coverage.linkedTrailers.some((lt) => lt.trailerId === t.id));
            drivers = data.equipment.drivers.filter((t) => coverage.linkedDrivers.some((lt) => lt.driverId === t.id));
        }

        return {
            tractors,
            trailers,
            drivers,
        };
    }

    private static isUnitActiveAt15DayCutoff(
        unit: MonthlyReportDataUnit<Tractor | Trailer | Driver>,
        coverageOptionId: CoverageOption.Id,
        isActiveAtEndOfMonth: boolean,
        fifteenthDayOfMonth: moment.Moment
    ): boolean {
        let isActive = isActiveAtEndOfMonth;

        // Walk changes after the cutoff backwards from the end-layer state. Rewinding an addition
        // makes the unit inactive; rewinding a removal makes it active. This preserves the actual
        // state on the 15th even when a unit is removed and re-added later in the same month.
        const changesAfterCutoff = unit.changes
            .filter((change) => moment.utc(change.date).isAfter(fifteenthDayOfMonth, "day"))
            .sort((a, b) => b.date.getTime() - a.date.getTime());

        for (const change of changesAfterCutoff) {
            const coverageChanges = change.changesByCoverage.get(coverageOptionId)?.changes || [];
            for (let index = coverageChanges.length - 1; index >= 0; index--) {
                if (coverageChanges[index].type === "new") isActive = false;
                if (coverageChanges[index].type === "delete") isActive = true;
            }
        }

        return isActive;
    }

    public static getProrateFactor(
        report: Pick<MonthlyReport, "year" | "month">,
        policy: Pick<PolicyLayer["policy"], "effectiveDate" | "expirationDate" | "cancelEffectiveDate">
    ): number {
        const expirationDate = moment.utc(policy.cancelEffectiveDate || policy.expirationDate).subtract(1, "day");
        const daysInMonth = moment
            .utc()
            .year(report.year)
            .month(report.month - 1)
            .daysInMonth();
        const startOfReportMonth = moment
            .utc()
            .year(report.year)
            .month(report.month - 1)
            .startOf("month")
            .startOf("day");
        const endOfReportMonth = moment
            .utc()
            .year(report.year)
            .month(report.month - 1)
            .endOf("month")
            .endOf("day");
        if (
            moment.utc(policy.effectiveDate).month() === startOfReportMonth.month() &&
            moment.utc(policy.effectiveDate).diff(startOfReportMonth, "days") > 0
        ) {
            const daysBeforeEffectiveDate = moment.utc(policy.effectiveDate).diff(startOfReportMonth, "days");
            return daysBeforeEffectiveDate / daysInMonth;
        } else if (expirationDate.month() === endOfReportMonth.month() && expirationDate.diff(endOfReportMonth, "days") < 0) {
            const daysAfterExpirationDate = Math.abs(expirationDate.diff(endOfReportMonth, "days"));
            return daysAfterExpirationDate / daysInMonth;
        }

        return 0;
    }

    /**
     * The units a single rating is charged on.
     *
     * This is the one place that decides which units a rating covers. Both the factor computation in
     * {@link getPremiumByCoverage} and the per-unit attribution in {@link getUnitPremiums} go through
     * it, so a unit can never be priced into the premium but left out of the attribution (or the
     * reverse) — that mismatch is what makes hand-rolled cost allocations drift.
     */
    private static getRatingUnits(
        coverageOptionId: CoverageOption.Id,
        basis: Coverage["basis"],
        rating: { description: string; tractorIds: number[]; trailerIds: number[] },
        units: {
            tractors: MonthlyReportDataUnit<Tractor>[];
            trailers: MonthlyReportDataUnit<Trailer>[];
            drivers: MonthlyReportDataUnit<Driver>[];
        },
        context: {
            isUniqueDeductibleRatingPresent: boolean;
            selectedUnitsRatings: { tractorIds: number[]; trailerIds: number[] }[];
        }
    ): {
        tractors: MonthlyReportDataUnit<Tractor>[];
        trailers: MonthlyReportDataUnit<Trailer>[];
        drivers: MonthlyReportDataUnit<Driver>[];
    } {
        const { isUniqueDeductibleRatingPresent, selectedUnitsRatings } = context;
        const description = rating.description.toLowerCase();

        // these coverages are rated on a straight head count, with no per-rating breakdown
        if (basis === Coverage.Basis.UNITS) {
            if (
                coverageOptionId === CoverageOption.Id.OCCUPATIONAL_ACCIDENT ||
                coverageOptionId === CoverageOption.Id.CONTINGENT_LIABILITY
            ) {
                return { tractors: [], trailers: [], drivers: units.drivers };
            }
            if (coverageOptionId === CoverageOption.Id.NON_TRUCKING_LIABILITY) {
                return { tractors: units.tractors, trailers: [], drivers: [] };
            }
        }

        const isSelectedElsewhere = (id: number, kind: "tractor" | "trailer") =>
            selectedUnitsRatings.some((r) => (kind === "tractor" ? r.tractorIds : r.trailerIds).includes(id));

        if (description === "selected vins") {
            return {
                tractors: units.tractors.filter((t) => rating.tractorIds.includes(t.id)),
                trailers: units.trailers.filter((t) => rating.trailerIds.includes(t.id)),
                drivers: [],
            };
        }

        if (description === "equipment with unique deductible") {
            return {
                tractors: units.tractors.filter((t) => !isSelectedElsewhere(t.id, "tractor") && t.hasUniqueDeductible),
                trailers: units.trailers.filter((t) => !isSelectedElsewhere(t.id, "trailer") && t.hasUniqueDeductible),
                drivers: [],
            };
        }

        // units carrying a unique deductible are priced by their own rating when one exists
        const isEligible = (unit: { id: number; hasUniqueDeductible: boolean }, kind: "tractor" | "trailer") =>
            !isSelectedElsewhere(unit.id, kind) && !(isUniqueDeductibleRatingPresent && unit.hasUniqueDeductible);

        if (description === "tractors") {
            return { tractors: units.tractors.filter((t) => isEligible(t, "tractor")), trailers: [], drivers: [] };
        }
        if (description === "trailers") {
            return { tractors: [], trailers: units.trailers.filter((t) => isEligible(t, "trailer")), drivers: [] };
        }

        return {
            tractors: units.tractors.filter((t) => isEligible(t, "tractor")),
            trailers: units.trailers.filter((t) => isEligible(t, "trailer")),
            drivers: [],
        };
    }

    /** A unit's contribution to a stated-value rating. ACV units carry no stated value. */
    private static getStatedValue(unit: { isACV: boolean; statedValue: number | null }): number {
        return unit.isACV ? 0 : unit.statedValue || 0;
    }

    public static getPremiumByCoverage(
        report: Pick<MonthlyReport, "revenue" | "mileage" | "premiumModifications" | "payroll">,
        data: Pick<MonthlyReportData, "endLayer" | "month" | "year" | "equipment">
    ): Map<CoverageOption.Id, MonthlyReportCoveragePremium> {
        const result = new Map<CoverageOption.Id, MonthlyReportCoveragePremium>();
        for (const coverage of data.endLayer.policy.coverages) {
            const coveragePremium: MonthlyReportCoveragePremium = {
                minimumPremium: 0,
                minimumPremiumApplied: 0,
                totalPremium: 0,
                subtotalPremium: 0,
                proratedPremiumApplied: 0,
                rates: [],
            };

            const ratings: {
                description: string;
                basis: Coverage["basis"];
                rate: number;
                tractorIds: number[];
                trailerIds: number[];
            }[] = [];

            if (coverage.compositeRatings?.length) {
                for (const rating of coverage.compositeRatings) {
                    if (rating.frequency !== Coverage.Frequency.MONTHLY) continue;
                    ratings.push({
                        description: rating.description,
                        rate: rating.rate,
                        basis: rating.basis,
                        tractorIds: rating.linkedTractors?.map((t) => t.tractorId) || [],
                        trailerIds: rating.linkedTrailers?.map((t) => t.trailerId) || [],
                    });
                }
            } else if (coverage.frequency === Coverage.Frequency.MONTHLY) {
                ratings.push({ description: "", rate: coverage.rate, basis: coverage.basis, tractorIds: [], trailerIds: [] });
            }

            if (!ratings.length) continue;

            const { tractors, trailers, drivers } = MonthlyReportHelper.getFinalUnits(data, coverage as Required<Coverage>);

            const prorateFactor = MonthlyReportHelper.getProrateFactor(data, data.endLayer.policy);

            const isUniqueDeductibleRatingPresent = ratings.some((r) => r.description.toLowerCase() === "equipment with unique deductible");
            const selectedUnitsRatings = ratings.filter(
                (r) => r.description.toLowerCase() === "selected vins" && (r.tractorIds.length || r.trailerIds.length)
            );

            for (const rating of ratings) {
                let rate: number;
                let factor: number;
                let ratedUnits: ReturnType<typeof MonthlyReportHelper.getRatingUnits> | null = null;

                if (rating.basis === Coverage.Basis.UNITS) {
                    rate = rating.rate || 0;
                    ratedUnits = MonthlyReportHelper.getRatingUnits(
                        coverage.coverageOptionId,
                        rating.basis,
                        rating,
                        { tractors, trailers, drivers },
                        { isUniqueDeductibleRatingPresent, selectedUnitsRatings }
                    );
                    factor = ratedUnits.tractors.length + ratedUnits.trailers.length + ratedUnits.drivers.length;
                } else if (rating.basis === Coverage.Basis.VALUES) {
                    rate = (rating.rate || 0) / 100;
                    ratedUnits = MonthlyReportHelper.getRatingUnits(
                        coverage.coverageOptionId,
                        rating.basis,
                        rating,
                        { tractors, trailers, drivers },
                        { isUniqueDeductibleRatingPresent, selectedUnitsRatings }
                    );
                    factor =
                        ratedUnits.tractors.reduce((acc, t) => acc + MonthlyReportHelper.getStatedValue(t), 0) +
                        ratedUnits.trailers.reduce((acc, t) => acc + MonthlyReportHelper.getStatedValue(t), 0);
                } else if (rating.basis === Coverage.Basis.MILEAGE) {
                    rate = rating.rate || 0;
                    factor = report.mileage;
                } else if (rating.basis === Coverage.Basis.REVENUE) {
                    rate = (rating.rate || 0) / 100;
                    factor = report.revenue;
                } else if (rating.basis === Coverage.Basis.INSTALLMENT) {
                    rate = 1;
                    factor = rating.rate || 0;
                } else if (rating.basis === Coverage.Basis.FEE) {
                    rate = 1;
                    factor = rating.rate || 0;
                } else if (rating.basis === Coverage.Basis.PREMIUM) {
                    rate = (rating.rate || 0) / 100;
                    factor = 0;
                } else if (rating.basis === Coverage.Basis.PAYROLL) {
                    rate = rating.rate || 0;
                    if (rating.description === "Terrorism") {
                        factor = report.payroll.reduce((acc, p) => acc + p.amount, 0);
                    } else {
                        factor = report.payroll.find((crp) => crp.description === rating.description)?.amount || 0;
                    }
                } else {
                    continue;
                }
                const total = rate * factor;
                let prorated = 0;
                switch (rating.basis) {
                    case Coverage.Basis.REVENUE:
                    case Coverage.Basis.MILEAGE:
                    case Coverage.Basis.FEE:
                    case Coverage.Basis.PREMIUM:
                    case Coverage.Basis.PAYROLL:
                        prorated = 0;
                        break;
                    default:
                        prorated = total * prorateFactor;
                }
                coveragePremium.rates.push({
                    description: rating.description,
                    basis: rating.basis,
                    rate,
                    factor,
                    prorated,
                    total,
                    isModification: false,
                    units: {
                        tractorIds: ratedUnits?.tractors.map((t) => t.id) ?? [],
                        trailerIds: ratedUnits?.trailers.map((t) => t.id) ?? [],
                        driverIds: ratedUnits?.drivers.map((d) => d.id) ?? [],
                    },
                });
            }

            for (const modification of report.premiumModifications) {
                if (modification.coverageOptionId === coverage.coverageOptionId) {
                    coveragePremium.rates.push({
                        description: modification.description,
                        basis: Coverage.Basis.FLAT,
                        rate: 1,
                        factor: modification.total,
                        prorated: 0,
                        total: modification.total,
                        isModification: true,
                        // a manual adjustment belongs to the coverage, not to any one unit
                        units: { tractorIds: [], trailerIds: [], driverIds: [] },
                    });
                }
            }

            const premiumRateFactor = coveragePremium.rates.reduce(
                (acc, r) => acc + (r.basis !== Coverage.Basis.FEE && r.basis !== Coverage.Basis.PREMIUM ? r.total : 0),
                0
            );
            for (const rate of coveragePremium.rates) {
                if (rate.basis === Coverage.Basis.PREMIUM) {
                    rate.factor = premiumRateFactor;
                    rate.total = rate.rate * rate.factor;
                    if (rate.description !== "Surplus Lines Tax") {
                        rate.prorated = rate.total * prorateFactor;
                    }
                }
            }

            coveragePremium.proratedPremiumApplied = coveragePremium.rates.reduce((acc, r) => acc + r.prorated, 0);
            coveragePremium.subtotalPremium = coveragePremium.rates.reduce((acc, r) => acc + r.total, 0);

            const minimumPremiumAttribute = coverage.attributes.find(
                (att) => att.attributeOptionId === AttributeOption.Id.MINIMUM_MONTHLY_PREMIUM_COVERAGE
            );
            if (minimumPremiumAttribute) {
                const minimumPremium = (minimumPremiumAttribute.valueNumber || 0) * (1 - prorateFactor);
                coveragePremium.minimumPremium = minimumPremium;

                // only some Basis types are applicable to be included in the minimum premium calculation
                // so we cannot just use `coveragePremium.subtotalPremium`. Manual credit adjustments are
                // also excluded here so an intentional credit can carry the total below the minimum
                // premium instead of being absorbed back out by the minimum premium top-up.
                const minimumPremiumApplicableTotal = coveragePremium.rates.reduce(
                    (acc, r) =>
                        acc +
                        (r.basis !== Coverage.Basis.FEE &&
                        !(r.basis === Coverage.Basis.PREMIUM && r.description === "Surplus Lines Tax") &&
                        !(r.isModification && r.total < 0)
                            ? r.total
                            : 0),
                    0
                );

                const minimumPremiumApplied = Math.max(0, minimumPremium - minimumPremiumApplicableTotal);
                coveragePremium.minimumPremiumApplied = minimumPremiumApplied;
            }

            coveragePremium.totalPremium =
                coveragePremium.subtotalPremium + coveragePremium.minimumPremiumApplied - coveragePremium.proratedPremiumApplied;

            result.set(coverage.coverageOptionId, coveragePremium);
        }
        return result;
    }

    /**
     * Splits each coverage's premium across the units it was charged on.
     *
     * Per-unit and stated-value rates are attributed exactly, using the same unit lists the premium
     * itself was computed from. Everything else on the coverage — minimum premium top-ups, manual
     * modifications, proration credits, and mileage/revenue/payroll components — cannot belong to any
     * one unit, so it is spread in proportion to what each unit was directly charged (evenly if
     * nothing was). That spread is an allocation, not a fact; `isExact` reports whether any was needed.
     *
     * Allocations are reconciled to the cent: each coverage's unit premiums sum exactly to its
     * `totalPremium`. Note that a policy's own minimum premium and its rounding adjustment sit above
     * the coverage level and are deliberately not distributed here.
     */
    public static getUnitPremiums(
        data: Pick<MonthlyReportData, "premiumByCoverage" | "equipment">
    ): Map<CoverageOption.Id, MonthlyReportCoverageUnitPremiums> {
        const result = new Map<CoverageOption.Id, MonthlyReportCoverageUnitPremiums>();

        const tractorsById = new Map(data.equipment.tractors.map((t) => [t.id, t]));
        const trailersById = new Map(data.equipment.trailers.map((t) => [t.id, t]));

        for (const [coverageOptionId, coveragePremium] of data.premiumByCoverage.entries()) {
            const direct = new Map<
                string,
                { unitId: number; unitType: MonthlyReportUnitPremium["unitType"]; amount: number; lines: MonthlyReportPremiumLine[] }
            >();

            const add = (
                unitId: number,
                unitType: MonthlyReportUnitPremium["unitType"],
                line: MonthlyReportPremiumLine
            ) => {
                const key = `${unitType}:${unitId}`;
                const existing = direct.get(key);
                if (existing) {
                    existing.amount += line.amount;
                    existing.lines.push(line);
                } else {
                    direct.set(key, { unitId, unitType, amount: line.amount, lines: [line] });
                }
            };

            for (const rate of coveragePremium.rates) {
                const units = rate.units;
                if (!units) continue;

                const line = (factor: number): MonthlyReportPremiumLine => ({
                    description: rate.description,
                    basis: rate.basis,
                    rate: rate.rate,
                    factor,
                    amount: rate.rate * factor,
                });

                if (rate.basis === Coverage.Basis.UNITS) {
                    // factor is the head count, so each unit's share is simply the rate
                    for (const id of units.tractorIds) add(id, "tractor", line(1));
                    for (const id of units.trailerIds) add(id, "trailer", line(1));
                    for (const id of units.driverIds) add(id, "driver", line(1));
                } else if (rate.basis === Coverage.Basis.VALUES) {
                    // factor is the summed stated value, so each unit's share is its own value
                    for (const id of units.tractorIds) {
                        const tractor = tractorsById.get(id);
                        if (tractor) add(id, "tractor", line(MonthlyReportHelper.getStatedValue(tractor)));
                    }
                    for (const id of units.trailerIds) {
                        const trailer = trailersById.get(id);
                        if (trailer) add(id, "trailer", line(MonthlyReportHelper.getStatedValue(trailer)));
                    }
                }
                // every other basis is charged to the policy, not to a unit; it falls into the residual
            }

            const entries = [...direct.values()];
            const directTotal = entries.reduce((acc, e) => acc + e.amount, 0);
            const residualTotal = coveragePremium.totalPremium - directTotal;

            const units: MonthlyReportUnitPremium[] = entries.map((entry) => {
                let residualPremium: number;
                if (residualTotal === 0 || !entries.length) {
                    residualPremium = 0;
                } else if (directTotal !== 0) {
                    residualPremium = residualTotal * (entry.amount / directTotal);
                } else {
                    residualPremium = residualTotal / entries.length;
                }
                return {
                    unitId: entry.unitId,
                    unitType: entry.unitType,
                    directPremium: entry.amount,
                    residualPremium,
                    totalPremium: entry.amount + residualPremium,
                    lines: entry.lines,
                };
            });

            MonthlyReportHelper.reconcileToCents(units, coveragePremium.totalPremium);

            result.set(coverageOptionId, {
                units,
                totalPremium: coveragePremium.totalPremium,
                residualPremium: residualTotal,
                isExact: Math.abs(residualTotal) < 0.005,
            });
        }

        return result;
    }

    /**
     * Nudges the largest allocation so the rounded per-unit amounts add up to the coverage total
     * exactly. Without this, billing several units from these numbers can drift by a cent or two.
     */
    private static reconcileToCents(units: MonthlyReportUnitPremium[], totalPremium: number): void {
        if (!units.length) return;

        let allocatedCents = 0;
        for (const unit of units) {
            const cents = Math.round(unit.totalPremium * 100);
            unit.totalPremium = cents / 100;
            allocatedCents += cents;
        }

        const driftCents = Math.round(totalPremium * 100) - allocatedCents;
        if (driftCents === 0) return;

        const largest = units.reduce((a, b) => (Math.abs(b.totalPremium) > Math.abs(a.totalPremium) ? b : a));
        largest.totalPremium = Math.round(largest.totalPremium * 100 + driftCents) / 100;
        largest.residualPremium = largest.totalPremium - largest.directPremium;
    }

    /**
     * What each driver costs, the way an independent-contractor bill is built: the driver's own
     * coverages plus the coverages on the equipment assigned to them. Equipment shared between
     * drivers is split evenly, so summing every driver never bills the same unit twice.
     *
     * `drivers` defaults to the drivers on this policy, but an account-level caller should pass the
     * drivers from every policy it is aggregating. A driver's equipment is frequently insured on a
     * different policy than the driver themselves — an owner-operator on an occupational accident
     * policy whose tractor carries physical damage on the auto policy — and that cost still belongs
     * to them, so the driver list must not be limited to the policy being priced.
     *
     * Returns a map of driverId to that driver's cost per coverage.
     */
    public static getDriverPremiums(
        data: Pick<MonthlyReportData, "premiumByCoverage" | "equipment">,
        drivers: MonthlyReportDataUnit<Driver>[] = data.equipment.drivers
    ): Map<number, Map<CoverageOption.Id, number>> {
        const breakdown = MonthlyReportHelper.getDriverPremiumBreakdown(data, drivers);
        const result = new Map<number, Map<CoverageOption.Id, number>>();

        for (const [driverId, byCoverage] of breakdown.entries()) {
            const totals = new Map<CoverageOption.Id, number>();
            for (const [coverageOptionId, cost] of byCoverage.entries()) {
                totals.set(coverageOptionId, cost.totalPremium);
            }
            result.set(driverId, totals);
        }

        return result;
    }

    /**
     * The same roll-up as {@link getDriverPremiums}, but keeping every unit and rating that fed into
     * each figure so the number can be shown its working — which unit, which rating, at what rate.
     */
    public static getDriverPremiumBreakdown(
        data: Pick<MonthlyReportData, "premiumByCoverage" | "equipment">,
        drivers: MonthlyReportDataUnit<Driver>[] = data.equipment.drivers
    ): Map<number, Map<CoverageOption.Id, MonthlyReportDriverCoverageCost>> {
        const unitPremiums = MonthlyReportHelper.getUnitPremiums(data);
        const result = new Map<number, Map<CoverageOption.Id, MonthlyReportDriverCoverageCost>>();

        // how many drivers share each unit, so its cost can be split rather than duplicated
        const driversPerTractor = new Map<number, number>();
        const driversPerTrailer = new Map<number, number>();
        for (const driver of drivers) {
            for (const tractor of driver.tractors || []) {
                driversPerTractor.set(tractor.id, (driversPerTractor.get(tractor.id) || 0) + 1);
            }
            for (const trailer of driver.trailers || []) {
                driversPerTrailer.set(trailer.id, (driversPerTrailer.get(trailer.id) || 0) + 1);
            }
        }

        for (const driver of drivers) {
            const byCoverage = new Map<CoverageOption.Id, MonthlyReportDriverCoverageCost>();

            for (const [coverageOptionId, coverage] of unitPremiums.entries()) {
                const contributions: MonthlyReportDriverCoverageCost["contributions"] = [];

                for (const unit of coverage.units) {
                    let sharedBy: number | null = null;

                    if (unit.unitType === "driver" && unit.unitId === driver.id) {
                        sharedBy = 1;
                    } else if (unit.unitType === "tractor" && (driver.tractors || []).some((t) => t.id === unit.unitId)) {
                        sharedBy = driversPerTractor.get(unit.unitId) || 1;
                    } else if (unit.unitType === "trailer" && (driver.trailers || []).some((t) => t.id === unit.unitId)) {
                        sharedBy = driversPerTrailer.get(unit.unitId) || 1;
                    }

                    if (sharedBy === null) continue;

                    contributions.push({
                        unitType: unit.unitType,
                        unitId: unit.unitId,
                        sharedBy,
                        totalPremium: unit.totalPremium / sharedBy,
                        directPremium: unit.directPremium / sharedBy,
                        residualPremium: unit.residualPremium / sharedBy,
                        lines: unit.lines,
                    });
                }

                const totalPremium = contributions.reduce((acc, c) => acc + c.totalPremium, 0);
                if (totalPremium !== 0) {
                    byCoverage.set(coverageOptionId, { totalPremium, contributions });
                }
            }

            result.set(driver.id, byCoverage);
        }

        return result;
    }

    public static getFillableMonths(
        effectiveDate: Date,
        expirationDate: Date,
        isAdvanced: boolean,
        isNTL: boolean,
        isRenewal: boolean,
        reports: MonthlyReport[],
        cancelDate?: Date,
        isDoubleAdvanced = false
    ): MonthlyReportAvailableMonth[] {
        const startDate = moment.utc(effectiveDate);
        const endDate = moment.utc(expirationDate);
        const months: MonthlyReportAvailableMonth[] = [];
        if (isAdvanced) {
            if (!isRenewal) {
                // hide the first month of advanced policies if they are not renewals
                startDate.add(1, "month");
            }
            while (startDate.isSameOrBefore(endDate, "month")) {
                if (startDate.date() === 1 && startDate.isSame(endDate, "day")) break; // expiration date is exclusive not inclusive
                if (cancelDate && startDate.isAfter(moment.utc(cancelDate), "month")) {
                    break;
                }
                if (startDate.isAfter(moment.utc("2024-10-01"), "month")) {
                    const report = reports.find((r) => r.year === startDate.year() && r.month === startDate.month() + 1);
                    months.push({
                        month: startDate.month() + 1,
                        year: startDate.year(),
                        status: MonthlyReportHelper.getReportStatus(
                            startDate.year(),
                            startDate.month() + 1,
                            !!report?.isFinalized,
                            !!report?.isSubmitted,
                            isAdvanced,
                            cancelDate,
                            isDoubleAdvanced
                        ),
                    });
                }
                startDate.add(1, "month");
            }
        } else {
            while (startDate.isSameOrBefore(endDate, "month")) {
                if (startDate.date() === 1 && startDate.isSame(endDate, "day")) break; // expiration date is exclusive not inclusive
                if (cancelDate && startDate.isAfter(moment.utc(cancelDate), "month")) {
                    break;
                }
                const report = reports.find((r) => r.year === startDate.year() && r.month === startDate.month() + 1);
                if (isNTL) {
                    if (startDate.isSameOrAfter(moment.utc("2025-01-01"), "month")) {
                        months.push({
                            month: startDate.month() + 1,
                            year: startDate.year(),
                            status: MonthlyReportHelper.getReportStatus(
                                startDate.year(),
                                startDate.month() + 1,
                                !!report?.isFinalized,
                                !!report?.isSubmitted,
                                isAdvanced,
                                cancelDate,
                                isDoubleAdvanced
                            ),
                        });
                    }
                } else {
                    if (startDate.isAfter(moment.utc("2024-06-01"), "month")) {
                        months.push({
                            month: startDate.month() + 1,
                            year: startDate.year(),
                            status: MonthlyReportHelper.getReportStatus(
                                startDate.year(),
                                startDate.month() + 1,
                                !!report?.isFinalized,
                                !!report?.isSubmitted,
                                isAdvanced,
                                cancelDate,
                                isDoubleAdvanced
                            ),
                        });
                    }
                }
                startDate.add(1, "month");
            }
        }
        return months;
    }

    public static getAllAvailableMonths(
        effectiveDate: Date,
        expirationDate: Date,
        isAdvanced: boolean,
        reports: MonthlyReport[],
        cancelDate?: Date,
        isDoubleAdvanced = false
    ): MonthlyReportAvailableMonth[] {
        const startDate = moment.utc(effectiveDate);
        const endDate = moment.utc(expirationDate);
        const months: MonthlyReportAvailableMonth[] = [];
        if (isAdvanced) {
            while (startDate.isBefore(endDate, "month")) {
                if (startDate.date() === 1 && startDate.isSame(endDate, "day")) break; // expiration date is exclusive not inclusive
                const report = reports.find((r) => r.year === startDate.year() && r.month === startDate.month() + 1);
                months.push({
                    month: startDate.month() + 1,
                    year: startDate.year(),
                    status: MonthlyReportHelper.getReportStatus(
                        startDate.year(),
                        startDate.month() + 1,
                        !!report?.isFinalized,
                        !!report?.isSubmitted,
                        isAdvanced,
                        cancelDate,
                        isDoubleAdvanced
                    ),
                });
                startDate.add(1, "month");
            }
        } else {
            while (startDate.isSameOrBefore(endDate, "month")) {
                if (startDate.date() === 1 && startDate.isSame(endDate, "day")) break; // expiration date is exclusive not inclusive
                const report = reports.find((r) => r.year === startDate.year() && r.month === startDate.month() + 1);
                months.push({
                    month: startDate.month() + 1,
                    year: startDate.year(),
                    status: MonthlyReportHelper.getReportStatus(
                        startDate.year(),
                        startDate.month() + 1,
                        !!report?.isFinalized,
                        !!report?.isSubmitted,
                        isAdvanced,
                        cancelDate,
                        isDoubleAdvanced
                    ),
                });
                startDate.add(1, "month");
            }
        }
        return months;
    }

    /**
     *
     * @param layers an array of all available PolicyLayers for the policy
     * @param month the month, where January is 1 and December is 12
     * @param year the year
     * @returns an array of layers that are to be used when calculating the changes for the given month. if exists, the earliest layer (the last in the array) will be from a month before the provided month, so as to provide a base for comparison for the first layer of the month. it is possible that fewer than two layers will be returned.
     */
    public static getLayersForMonth(layers: PolicyLayer[], month: number, year: number): PolicyLayer[] {
        const m = moment.utc({ year, month: month - 1, date: 1, hour: 12, minute: 0, millisecond: 0 });
        let earliestLayer = layers[layers.length - 1];
        for (let i = layers.length - 2; i > 1; i--) {
            if (moment.utc(layers[i].effectiveDate).isSameOrAfter(m, "month")) {
                break;
            }
            earliestLayer = layers[i];
        }
        let latestLayer = layers[0];
        for (let i = 0; i < layers.length - 1; i++) {
            latestLayer = layers[i];
            if (layers[i].effectiveDate.getTime() <= earliestLayer.effectiveDate.getTime()) {
                break;
            }
            if (moment.utc(layers[i].effectiveDate).isSameOrBefore(m, "month")) {
                break;
            }
        }

        return layers.filter(
            (layer) =>
                layer.effectiveDate.getTime() >= earliestLayer.effectiveDate.getTime() &&
                layer.effectiveDate.getTime() <= latestLayer.effectiveDate.getTime()
        );
    }

    /**
     *
     * @param layers The array of layers to compare.
     * @param equipment The equipment to link to the changes.
     */
    private static getChanges(layers: PolicyLayer[], equipment: PolicyLayerComparison.IEquipment): MonthlyReportChanges {
        const result: MonthlyReportChanges = {
            tractors: [],
            trailers: [],
            drivers: [],
        };

        if (layers.length < 2) {
            return result;
        }

        for (let i = layers.length - 1; i > 0; i--) {
            const baseLayer = layers[i];
            const comparisonLayer = layers[i - 1];
            const comparison = PolicyLayer.compare(
                {
                    baseLayer,
                    comparisonLayer,
                },
                {
                    data: {
                        equipment,
                    },
                    separateEquipment: true,
                    accounting: false,
                    additionalInterests: false,
                    coverages: false,
                }
            );

            const changes = Array.from(comparison.equipment.getChangeList().values());

            for (const change of changes) {
                if ("driverId" in change.entity) {
                    result.drivers.push({
                        date: comparisonLayer.effectiveDate,
                        unitId: change.entity.driverId,
                        changesByCoverage: change.changeMap,
                        isNewToPolicy: !!change.entity.comparison?.isNewToPolicy,
                        isRemovedFromPolicy: !!change.entity.comparison?.isDeletedFromPolicy,
                    });
                } else if ("tractorId" in change.entity) {
                    result.tractors.push({
                        date: comparisonLayer.effectiveDate,
                        unitId: change.entity.tractorId,
                        changesByCoverage: change.changeMap,
                        isNewToPolicy: !!change.entity.comparison?.isNewToPolicy,
                        isRemovedFromPolicy: !!change.entity.comparison?.isDeletedFromPolicy,
                    });
                } else if ("trailerId" in change.entity) {
                    result.trailers.push({
                        date: comparisonLayer.effectiveDate,
                        unitId: change.entity.trailerId,
                        changesByCoverage: change.changeMap,
                        isNewToPolicy: !!change.entity.comparison?.isNewToPolicy,
                        isRemovedFromPolicy: !!change.entity.comparison?.isDeletedFromPolicy,
                    });
                }
            }
        }

        return result;
    }

    public static getIsAdvancedReporter(policy: PolicyLayer["policy"]): boolean {
        return policy.attributes.some(
            (a) =>
                a.attributeOptionId === AttributeOption.Id.COMPOSITE_ADVANCED ||
                a.attributeOptionId === AttributeOption.Id.COMPOSITE_DOUBLE_ADVANCED
        );
    }

    public static getIsDoubleAdvancedReporter(policy: PolicyLayer["policy"]): boolean {
        return policy.attributes.some((a) => a.attributeOptionId === AttributeOption.Id.COMPOSITE_DOUBLE_ADVANCED);
    }

    public static getMonthlyReportData(
        report: Pick<MonthlyReport, "year" | "month" | "mileage" | "revenue" | "finalizedDate" | "premiumModifications" | "payroll">,
        layers: PolicyLayer[],
        equipment: PolicyLayerComparison.IEquipment
    ): MonthlyReportData {
        const layersForMonth = MonthlyReportHelper.getLayersForMonth(layers, report.month, report.year);
        const startLayer = layersForMonth[layersForMonth.length - 1];
        const endLayer = layersForMonth[0];

        const changes = MonthlyReportHelper.getChanges(layersForMonth, equipment);
        const result: MonthlyReportData = {
            policyId: startLayer.policy.id,
            month: report.month,
            year: report.year,
            startLayer,
            endLayer,
            subtotalPremium: 0,
            totalPremium: 0,
            minimumPremium: 0,
            minimumPremiumApplied: 0,
            proratedPremiumApplied: 0,
            roundingAdjustment: 0,
            premiumByCoverage: new Map(),
            isAdvancedReporter: this.getIsAdvancedReporter(endLayer.policy),
            isDriverReporter: endLayer.policy.coverages.some(
                (cov) =>
                    cov.coverageOptionId === CoverageOption.Id.OCCUPATIONAL_ACCIDENT ||
                    cov.coverageOptionId === CoverageOption.Id.CONTINGENT_LIABILITY ||
                    cov.coverageOptionId === CoverageOption.Id.NON_TRUCKING_LIABILITY
            ),
            isUnitReporter: endLayer.policy.coverages.some(
                (c) =>
                    Coverage.isComposite(endLayer.policy, c) &&
                    (c.basis === Coverage.Basis.UNITS ||
                        c.basis === Coverage.Basis.VALUES ||
                        (c.compositeRatings &&
                            c.compositeRatings.some((cr) => cr.basis === Coverage.Basis.UNITS || cr.basis === Coverage.Basis.VALUES)))
            ),
            isMileageReporter: endLayer.policy.coverages.some(
                (c) =>
                    Coverage.isComposite(endLayer.policy, c) &&
                    (c.basis === Coverage.Basis.MILEAGE ||
                        (c.compositeRatings && c.compositeRatings.some((cr) => cr.basis === Coverage.Basis.MILEAGE)))
            ),
            isRevenueReporter: endLayer.policy.coverages.some(
                (c) =>
                    Coverage.isComposite(endLayer.policy, c) &&
                    (c.basis === Coverage.Basis.REVENUE ||
                        (c.compositeRatings && c.compositeRatings.some((cr) => cr.basis === Coverage.Basis.REVENUE)))
            ),
            isPayrollReporter: endLayer.policy.coverages.some(
                (c) =>
                    Coverage.isComposite(endLayer.policy, c) &&
                    (c.basis === Coverage.Basis.PAYROLL ||
                        (c.compositeRatings && c.compositeRatings.some((cr) => cr.basis === Coverage.Basis.PAYROLL)))
            ),
            equipment: {
                tractors: equipment.tractors.map((u) => ({
                    ...u,
                    changes: [],
                    isNewToPolicy: false,
                    dateAddedToPolicy: null,
                    isRemovedFromPolicy: false,
                    dateRemovedFromPolicy: null,
                    isNewToCoverage: new Map(),
                    isRemovedFromCoverage: new Map(),
                    isACV: false,
                    isSpare: false,
                    statedValue: null,
                    previousIsACV: false,
                    previousStatedValue: null,
                    hasUniqueDeductible: false,
                    experience: undefined as never,
                    // employer/employerId are deliberately not overridden — equipment carries its own,
                    // and blanking them here made the equipment employer column read as empty
                    isOwnerOperator: undefined as never,
                    isTractor: true,
                    isTrailer: false,
                    isDriver: false,
                    coverages: [],
                    additionalInterests: [],
                })),
                trailers: equipment.trailers.map((u) => ({
                    ...u,
                    changes: [],
                    isNewToPolicy: false,
                    dateAddedToPolicy: null,
                    isRemovedFromPolicy: false,
                    dateRemovedFromPolicy: null,
                    isNewToCoverage: new Map(),
                    isRemovedFromCoverage: new Map(),
                    isACV: false,
                    isSpare: false,
                    statedValue: null,
                    previousIsACV: false,
                    previousStatedValue: null,
                    hasUniqueDeductible: false,
                    experience: undefined as never,
                    // employer/employerId are deliberately not overridden — equipment carries its own,
                    // and blanking them here made the equipment employer column read as empty
                    isOwnerOperator: undefined as never,
                    isTractor: false,
                    isTrailer: true,
                    isDriver: false,
                    coverages: [],
                    additionalInterests: [],
                })),
                drivers: equipment.drivers.map((u) => ({
                    ...u,
                    changes: [],
                    isNewToPolicy: false,
                    dateAddedToPolicy: null,
                    isRemovedFromPolicy: false,
                    dateRemovedFromPolicy: null,
                    isNewToCoverage: new Map(),
                    isRemovedFromCoverage: new Map(),
                    isACV: undefined as never,
                    isSpare: undefined as never,
                    statedValue: undefined as never,
                    previousIsACV: undefined as never,
                    previousStatedValue: undefined as never,
                    hasUniqueDeductible: undefined as never,
                    isOwnerOperator: false,
                    experience: null,
                    employer: u.employer,
                    employerId: u.employerId,
                    isTractor: false,
                    isTrailer: false,
                    isDriver: true,
                    coverages: [],
                    additionalInterests: [],
                })),
                activeTractors: [],
                activeTrailers: [],
                activeDrivers: [],
            },
        };

        for (const change of changes.drivers) {
            result.equipment.drivers.find((d) => d.id === change.unitId)?.changes.push(change);
        }
        for (const change of changes.tractors) {
            result.equipment.tractors.find((t) => t.id === change.unitId)?.changes.push(change);
        }
        for (const change of changes.trailers) {
            result.equipment.trailers.find((t) => t.id === change.unitId)?.changes.push(change);
        }

        result.equipment.drivers = result.equipment.drivers
            .filter((driver) => driver.changes.length > 0 || MonthlyReportHelper.isDriverOnPolicy(driver, endLayer))
            .map((unit) => {
                const isRemovedFromPolicy = unit.changes.some((change) => change.isRemovedFromPolicy);
                const isNewToPolicy = unit.changes.some((change) => change.isNewToPolicy);
                return {
                    ...unit,
                    isOwnerOperator: MonthlyReportHelper.isDriverOwnerOperatorForMonth(
                        unit,
                        layersForMonth,
                        isNewToPolicy,
                        isRemovedFromPolicy
                    ),
                    experience: isRemovedFromPolicy ? getDriverExperience(unit, startLayer) : getDriverExperience(unit, endLayer),
                    isNewToPolicy,
                    dateAddedToPolicy: unit.changes.find((change) => change.isNewToPolicy)?.date || null,
                    isRemovedFromPolicy,
                    dateRemovedFromPolicy: unit.changes.find((change) => change.isRemovedFromPolicy)?.date || null,
                    isRemovedFromCoverage: unit.changes.reduce((acc, change) => {
                        for (const [coverageId, value] of change.changesByCoverage) {
                            if (value.changes.some((ch) => ch.type === "delete")) {
                                acc.set(coverageId, true);
                            }
                        }
                        return acc;
                    }, new Map()),
                    isNewToCoverage: unit.changes.reduce((acc, change) => {
                        for (const [coverageId, value] of change.changesByCoverage) {
                            if (value.changes.some((ch) => ch.type === "new")) {
                                acc.set(coverageId, true);
                            }
                        }
                        return acc;
                    }, new Map()),
                    coverages: endLayer.policy.coverages
                        .filter((c) => c.linkedDrivers?.some((d) => d.driverId === unit.id))
                        .map((c) => ({
                            policyId: c.policyId,
                            coverageOptionId: c.coverageOptionId,
                            effectiveDate: endLayer.policy.effectiveDate.toISOString().substring(0, 10),
                            isUpcoming: moment(endLayer.policy.effectiveDate).isAfter(moment(), "day"),
                        })),
                };
            });
        result.equipment.tractors = result.equipment.tractors
            .filter((tractor) => tractor.changes.length > 0 || MonthlyReportHelper.isTractorOnPolicy(tractor, endLayer))
            .map((unit) => {
                const isSpare = endLayer.policy.coverages.some((coverage) =>
                    coverage.attributes.some(
                        (attr) => attr.tractorId === unit.id && attr.attributeOptionId === AttributeOption.Id.UNIT_SPARE_RATED
                    )
                );
                const endLayerAPD = endLayer.policy.coverages.find(
                    (cov) => cov.coverageOptionId === CoverageOption.Id.TRUCKERS_PHYSICAL_DAMAGE
                );
                const isACV = endLayerAPD?.attributes.some(
                    (attr) => attr.tractorId === unit.id && attr.attributeOptionId === AttributeOption.Id.VALUATION_ACTUAL_CASH_VALUE
                );
                const statedValue = endLayerAPD?.attributes.find(
                    (attr) => attr.tractorId === unit.id && attr.attributeOptionId === AttributeOption.Id.STATED_VALUE
                )?.valueNumber;
                const hasUniqueDeductible = endLayerAPD?.attributes.some(
                    (attr) =>
                        attr.tractorId === unit.id &&
                        (attr.attributeOptionId === AttributeOption.Id.UNIT_COMPREHENSIVE ||
                            attr.attributeOptionId === AttributeOption.Id.UNIT_COLLISION) &&
                        attr.valueNumber
                );

                const startLayerAPD = startLayer.policy.coverages.find(
                    (cov) => cov.coverageOptionId === CoverageOption.Id.TRUCKERS_PHYSICAL_DAMAGE
                );
                const previousIsACV = startLayerAPD?.attributes.some(
                    (attr) => attr.tractorId === unit.id && attr.attributeOptionId === AttributeOption.Id.VALUATION_ACTUAL_CASH_VALUE
                );
                const previousStatedValue = startLayerAPD?.attributes.find(
                    (attr) => attr.tractorId === unit.id && attr.attributeOptionId === AttributeOption.Id.STATED_VALUE
                )?.valueNumber;

                const isRemovedFromPolicy = unit.changes.some((change) => change.isRemovedFromPolicy);

                return {
                    ...unit,
                    isNewToPolicy: unit.changes.some((change) => change.isNewToPolicy),
                    dateAddedToPolicy: unit.changes.find((change) => change.isNewToPolicy)?.date || null,
                    isRemovedFromPolicy,
                    dateRemovedFromPolicy: unit.changes.find((change) => change.isRemovedFromPolicy)?.date || null,
                    isRemovedFromCoverage: unit.changes.reduce((acc, change) => {
                        for (const [coverageId, value] of change.changesByCoverage) {
                            if (value.changes.some((ch) => ch.type === "delete")) {
                                acc.set(coverageId, true);
                            }
                        }
                        return acc;
                    }, new Map()),
                    isNewToCoverage: unit.changes.reduce((acc, change) => {
                        for (const [coverageId, value] of change.changesByCoverage) {
                            if (value.changes.some((ch) => ch.type === "new")) {
                                acc.set(coverageId, true);
                            }
                        }
                        return acc;
                    }, new Map()),
                    isSpare: !!isSpare,
                    isACV: isRemovedFromPolicy ? !!previousIsACV : !!isACV,
                    statedValue: isRemovedFromPolicy ? previousStatedValue || null : statedValue || null,
                    previousIsACV: previousIsACV,
                    previousStatedValue: previousStatedValue,
                    hasUniqueDeductible: !!hasUniqueDeductible,
                    coverages: endLayer.policy.coverages
                        .filter((c) => c.linkedTractors?.some((t) => t.tractorId === unit.id))
                        .map((c) => ({
                            policyId: c.policyId,
                            coverageOptionId: c.coverageOptionId,
                            effectiveDate: endLayer.policy.effectiveDate.toISOString().substring(0, 10),
                            isUpcoming: moment(endLayer.policy.effectiveDate).isAfter(moment(), "day"),
                        })),
                    additionalInterests: endLayer.policy.additionalInterests.filter((ai) =>
                        ai.linkedTractors?.some((lt) => lt.tractorId === unit.id)
                    ),
                };
            });
        result.equipment.trailers = result.equipment.trailers
            .filter((trailer) => trailer.changes.length > 0 || MonthlyReportHelper.isTrailerOnPolicy(trailer, endLayer))
            .map((unit) => {
                const isSpare = endLayer.policy.coverages.some((coverage) =>
                    coverage.attributes.some(
                        (attr) => attr.trailerId === unit.id && attr.attributeOptionId === AttributeOption.Id.UNIT_SPARE_RATED
                    )
                );
                const endLayerAPD = endLayer.policy.coverages.find(
                    (cov) => cov.coverageOptionId === CoverageOption.Id.TRUCKERS_PHYSICAL_DAMAGE
                );
                const isACV = endLayerAPD?.attributes.some(
                    (attr) => attr.trailerId === unit.id && attr.attributeOptionId === AttributeOption.Id.VALUATION_ACTUAL_CASH_VALUE
                );
                const statedValue = endLayerAPD?.attributes.find(
                    (attr) => attr.trailerId === unit.id && attr.attributeOptionId === AttributeOption.Id.STATED_VALUE
                )?.valueNumber;
                const hasUniqueDeductible = endLayerAPD?.attributes.some(
                    (attr) =>
                        attr.trailerId === unit.id &&
                        (attr.attributeOptionId === AttributeOption.Id.UNIT_COMPREHENSIVE ||
                            attr.attributeOptionId === AttributeOption.Id.UNIT_COLLISION) &&
                        attr.valueNumber
                );

                const startLayerAPD = startLayer.policy.coverages.find(
                    (cov) => cov.coverageOptionId === CoverageOption.Id.TRUCKERS_PHYSICAL_DAMAGE
                );
                const previousIsACV = startLayerAPD?.attributes.some(
                    (attr) => attr.trailerId === unit.id && attr.attributeOptionId === AttributeOption.Id.VALUATION_ACTUAL_CASH_VALUE
                );
                const previousStatedValue = startLayerAPD?.attributes.find(
                    (attr) => attr.trailerId === unit.id && attr.attributeOptionId === AttributeOption.Id.STATED_VALUE
                )?.valueNumber;

                const isRemovedFromPolicy = unit.changes.some((change) => change.isRemovedFromPolicy);

                return {
                    ...unit,
                    isNewToPolicy: unit.changes.some((change) => change.isNewToPolicy),
                    dateAddedToPolicy: unit.changes.find((change) => change.isNewToPolicy)?.date || null,
                    isRemovedFromPolicy,
                    dateRemovedFromPolicy: unit.changes.find((change) => change.isRemovedFromPolicy)?.date || null,
                    isRemovedFromCoverage: unit.changes.reduce((acc, change) => {
                        for (const [coverageId, value] of change.changesByCoverage) {
                            if (value.changes.some((ch) => ch.type === "delete")) {
                                acc.set(coverageId, true);
                            }
                        }
                        return acc;
                    }, new Map()),
                    isNewToCoverage: unit.changes.reduce((acc, change) => {
                        for (const [coverageId, value] of change.changesByCoverage) {
                            if (value.changes.some((ch) => ch.type === "new")) {
                                acc.set(coverageId, true);
                            }
                        }
                        return acc;
                    }, new Map()),
                    isSpare: !!isSpare,
                    isACV: isRemovedFromPolicy ? !!previousIsACV : !!isACV,
                    statedValue: isRemovedFromPolicy ? previousStatedValue || null : statedValue || null,
                    previousIsACV: previousIsACV,
                    previousStatedValue: previousStatedValue,
                    hasUniqueDeductible: !!hasUniqueDeductible,
                    coverages: endLayer.policy.coverages
                        .filter((c) => c.linkedTrailers?.some((t) => t.trailerId === unit.id))
                        .map((c) => ({
                            policyId: c.policyId,
                            coverageOptionId: c.coverageOptionId,
                            effectiveDate: endLayer.policy.effectiveDate.toISOString().substring(0, 10),
                            isUpcoming: moment(endLayer.policy.effectiveDate).isAfter(moment(), "day"),
                        })),
                    additionalInterests: endLayer.policy.additionalInterests.filter((ai) =>
                        ai.linkedTrailers?.some((lt) => lt.trailerId === unit.id)
                    ),
                };
            });

        result.equipment.activeTractors = result.equipment.tractors.filter((t) => MonthlyReportHelper.isTractorOnPolicy(t, endLayer));
        result.equipment.activeTrailers = result.equipment.trailers.filter((t) => MonthlyReportHelper.isTrailerOnPolicy(t, endLayer));
        result.equipment.activeDrivers = result.equipment.drivers.filter((d) => MonthlyReportHelper.isDriverOnPolicy(d, endLayer));

        const prorateFactor = MonthlyReportHelper.getProrateFactor(report, endLayer.policy);
        result.premiumByCoverage = MonthlyReportHelper.getPremiumByCoverage(report, result);

        const premium = MonthlyReportHelper.getPremium(
            result.premiumByCoverage,
            prorateFactor,
            endLayer.policy.attributes.find((att) => att.attributeOptionId === AttributeOption.Id.MINIMUM_MONTHLY_PREMIUM)?.valueNumber ||
                0,
            endLayer.policy.writingCompany.name,
            endLayer.policy.coverages.map((coverage) => coverage.coverageOptionId)
        );

        result.subtotalPremium = premium.subtotalPremium;
        result.minimumPremium = premium.minimumPremium;
        result.totalPremium = premium.totalPremium;
        result.proratedPremiumApplied = premium.proratedPremiumApplied;
        result.minimumPremiumApplied = premium.minimumPremiumApplied;
        result.roundingAdjustment = premium.roundingAdjustment;

        return result;
    }

    public static getPremium(
        premiumByCoverage: MonthlyReportData["premiumByCoverage"],
        prorateFactor: number,
        minimumMonthlyPremium: number,
        writingCompanyName: string,
        coverageOptionIds: CoverageOption.Id[]
    ): Pick<
        MonthlyReportData,
        "subtotalPremium" | "totalPremium" | "proratedPremiumApplied" | "minimumPremium" | "minimumPremiumApplied" | "roundingAdjustment"
    > {
        const result: Pick<
            MonthlyReportData,
            | "subtotalPremium"
            | "totalPremium"
            | "proratedPremiumApplied"
            | "minimumPremium"
            | "minimumPremiumApplied"
            | "roundingAdjustment"
        > = {
            subtotalPremium: 0,
            minimumPremium: 0,
            minimumPremiumApplied: 0,
            proratedPremiumApplied: 0,
            roundingAdjustment: 0,
            totalPremium: 0,
        };
        result.subtotalPremium = Array.from(premiumByCoverage.values())
            .flat()
            .reduce((acc, r) => acc + r.subtotalPremium, 0);

        result.minimumPremium = (minimumMonthlyPremium || 0) * (1 - prorateFactor);

        result.totalPremium = Array.from(premiumByCoverage.values())
            .flat()
            .reduce((acc, r) => acc + r.totalPremium, 0);

        result.proratedPremiumApplied = Array.from(premiumByCoverage.values())
            .map((p) => p.proratedPremiumApplied)
            .reduce((acc, val) => acc + val, 0);

        if (Array.from(premiumByCoverage.values()).some((p) => p.minimumPremiumApplied > 0)) {
            result.minimumPremiumApplied = Array.from(premiumByCoverage.values())
                .map((p) => p.minimumPremiumApplied)
                .reduce((acc, val) => acc + val, 0);
        }

        const totalCreditModifications = Array.from(premiumByCoverage.values())
            .flatMap((p) => p.rates)
            .reduce((acc, r) => acc + (r.isModification && r.total < 0 ? r.total : 0), 0);
        const totalPremiumExcludingCredits = result.totalPremium - totalCreditModifications;

        if (result.minimumPremium && totalPremiumExcludingCredits < result.minimumPremium) {
            result.minimumPremiumApplied = result.minimumPremium - totalPremiumExcludingCredits;
            result.totalPremium = result.minimumPremium + totalCreditModifications;
        }
        const isGreatWestRoundingExcluded = coverageOptionIds.some(
            (coverageOptionId) =>
                coverageOptionId === CoverageOption.Id.OCCUPATIONAL_ACCIDENT || coverageOptionId === CoverageOption.Id.CONTINGENT_LIABILITY
        );
        if (writingCompanyName === "Great West Casualty Company" && !isGreatWestRoundingExcluded) {
            const remainderCents = (result.totalPremium * 100) % 100;
            if (remainderCents === 0) {
                result.roundingAdjustment = 0;
            } else if (remainderCents < 50) {
                result.roundingAdjustment = -remainderCents / 100;
            } else {
                result.roundingAdjustment = (100 - remainderCents) / 100;
            }
        }
        result.totalPremium += result.roundingAdjustment;
        return result;
    }

    public static getUnitsForCoverage(
        data: MonthlyReportData,
        unitType: "tractors" | "trailers" | "drivers",
        coverageId?: CoverageOption.Id
    ) {
        const unitsFilteredByCoverage = (
            unitType === "tractors"
                ? data.equipment.tractors.filter((tractor) => {
                      if (!coverageId) {
                          return true;
                      }
                      return data.endLayer.policy.coverages.some(
                          (cov) =>
                              cov.coverageOptionId === coverageId &&
                              (cov.linkedTractors.some((lt) => lt.tractorId === tractor.id) ||
                                  tractor.changes.some((change) => change.changesByCoverage.has(coverageId)))
                      );
                  })
                : unitType === "trailers"
                ? data.equipment.trailers.filter((unit) => {
                      if (!coverageId) {
                          return true;
                      }
                      return data.endLayer.policy.coverages.some(
                          (cov) =>
                              cov.coverageOptionId === coverageId &&
                              (cov.linkedTrailers.some((lt) => lt.trailerId === unit.id) ||
                                  unit.changes.some((change) => change.changesByCoverage.has(coverageId)))
                      );
                  })
                : data.equipment.drivers.filter((driver) => {
                      if (!coverageId) {
                          return true;
                      }
                      return data.endLayer.policy.coverages.some(
                          (cov) =>
                              cov.coverageOptionId === coverageId &&
                              (cov.linkedDrivers.some((ld) => ld.driverId === driver.id) ||
                                  driver.changes.some((change) => change.changesByCoverage.has(coverageId)))
                      );
                  })
        ) as MonthlyReportDataUnit<Tractor | Trailer | Driver>[];

        return sortBy(
            unitsFilteredByCoverage,
            (x) => (coverageId ? x.isNewToCoverage.has(coverageId) : x.isNewToPolicy),
            (x) => (coverageId ? x.isRemovedFromCoverage.has(coverageId) : x.isRemovedFromPolicy),
            (x) => x.changes.length > 0
        ).reverse();
    }
    public static isDriverOnPolicy(driver: Driver, layer: PolicyLayer): boolean {
        return layer && layer.policy.coverages.some((cov) => cov.linkedDrivers.some((ld) => ld.driverId === driver.id));
    }
    public static isDriverOwnerOperatorForMonth(
        driver: Driver,
        layersForMonth: PolicyLayer[],
        isNewToPolicy: boolean,
        isRemovedFromPolicy: boolean
    ): boolean {
        const startLayer = layersForMonth[layersForMonth.length - 1];
        const endLayer = layersForMonth[0];

        if (!isRemovedFromPolicy) return getIsDriverOwnerOperator(driver, endLayer);

        // A driver added and removed in the same month is absent from both boundary layers. Preserve
        // the policy-level IC designation from the layers where the driver was actually covered.
        if (isNewToPolicy) return layersForMonth.some((layer) => getIsDriverOwnerOperator(driver, layer));

        return getIsDriverOwnerOperator(driver, startLayer);
    }
    public static isTractorOnPolicy(tractor: Tractor, layer: PolicyLayer): boolean {
        return layer && layer.policy.coverages.some((cov) => cov.linkedTractors.some((lt) => lt.tractorId === tractor.id));
    }
    public static isTrailerOnPolicy(trailer: Trailer, layer: PolicyLayer): boolean {
        return layer && layer.policy.coverages.some((cov) => cov.linkedTrailers.some((lt) => lt.trailerId === trailer.id));
    }

    public static getHasReportDue(
        availableMonths: MonthlyReportAvailableMonth[],
        reports: MonthlyReport[],
        isAdvanced: boolean,
        cancelDate?: Date,
        isDoubleAdvanced = false
    ): boolean {
        if (!availableMonths.length) return false;
        return availableMonths.some(({ year, month }) => {
            const report = reports.find((r) => r.year === year && r.month === month);
            return (
                MonthlyReportHelper.getReportStatus(
                    year,
                    month,
                    !!report?.isFinalized,
                    !!report?.isSubmitted,
                    isAdvanced,
                    cancelDate,
                    isDoubleAdvanced
                ) ===
                MonthlyReportStatus.Due
            );
        });
    }

    public static getReportStatus(
        year: number,
        month: number,
        isFinalized: boolean,
        isSubmitted: boolean,
        isAdvanced: boolean,
        cancelDate?: Date,
        isDoubleAdvanced = false
    ): MonthlyReportStatus {
        if (isFinalized) {
            return MonthlyReportStatus.Finalized;
        }
        if (isSubmitted) {
            return MonthlyReportStatus.Submitted;
        }
        const m = moment.utc({
            year: year,
            month: month - 1,
            date: 1,
            hour: 12,
            minute: 0,
            second: 0,
            millisecond: 0,
        });
        if (cancelDate && m.isAfter(moment.utc(cancelDate), "month")) {
            return MonthlyReportStatus.NotRequired;
        }

        if (isAdvanced) {
            if (isDoubleAdvanced) {
                if (m.isAfter(moment.utc().add(1, "month"), "month")) {
                    return MonthlyReportStatus.Upcoming;
                } else if (m.isBefore(moment.utc(), "month")) {
                    return MonthlyReportStatus.Overdue;
                } else {
                    return MonthlyReportStatus.Due;
                }
            }
            if (m.isAfter(moment.utc(), "month")) {
                return MonthlyReportStatus.Upcoming;
            } else if (m.isBefore(moment.utc().subtract(1, "months"), "month")) {
                return MonthlyReportStatus.Overdue;
            } else {
                return MonthlyReportStatus.Due;
            }
        } else {
            if (m.isAfter(moment.utc().subtract(1, "month"), "month")) {
                return MonthlyReportStatus.Upcoming;
            } else if (m.isBefore(moment.utc().subtract(1, "month"), "month")) {
                return MonthlyReportStatus.Overdue;
            } else {
                return MonthlyReportStatus.Due;
            }
        }
    }
}

function getDriverExperience(unit: Driver, layer: PolicyLayer): number | null {
    for (const coverage of layer.policy.coverages) {
        if (coverage.linkedDrivers.some((ld) => ld.driverId === unit.id)) {
            const exp = coverage.attributes.find(
                (attr) => attr.driverId === unit.id && attr.attributeOptionId === AttributeOption.Id.DRIVER_YEARS_EXPERIENCE
            );
            if (exp) {
                return exp.valueNumber || 0;
            }
        }
    }
    return null;
}

function getIsDriverOwnerOperator(driver: Driver, layer: PolicyLayer): boolean {
    for (const coverage of layer.policy.coverages) {
        if (coverage.linkedDrivers.some((ld) => ld.driverId === driver.id)) {
            if (
                coverage.attributes.some(
                    (attr) => attr.driverId === driver.id && attr.attributeOptionId === AttributeOption.Id.UNIT_OWNER_OPERATOR
                )
            ) {
                return true;
            }
        }
    }
    return false;
}
